import React, { useEffect, useState, useCallback, useRef } from 'react';
import { nanoid } from 'nanoid';
import AssistantMessage from '../components/AssistantMessage';
import UserMessage from '../components/UserMessage';
import { useResearchConsent } from '../hooks';
import { useLocalization } from '../hooks/queries/useLocalization';
import ResearchConsentMessage from '../components/research-consent/ResearchConsentMessage';
import ChatInput from '../components/ChatInput';
import { type RecordingState } from '../components/VoiceRecorder';
import { hideInfoCardsForMessage, getSessionToken } from '../services/backendService';
import { validateChatMessage } from '../services/validationService';
import SuicideQuestions from '../components/SuicideQuestions';
import type { ConversationItem } from '../types/conversationItem.types';
import { wait } from '../utils/wait';
import { useTranslate, useLanguage } from '../lib/i18n';
import { useCreateMessage, useCreateConversation } from '../hooks';
import { suppressDuplicateInfoCards } from '../utils/infoCardSuppression';
import { ChatbotConfig } from 'types/chatbotConfig.types';

const TYPING_INDICATOR_DELAY = 1000;
const PRESET_REPLY_DELAY = 3000;
const PRESET_REPLY_POST_TYPING_DELAY = Math.max(0, PRESET_REPLY_DELAY - TYPING_INDICATOR_DELAY);

const getPresetReplies = (bundle: Record<string, unknown> | undefined | null) => {
  if (!bundle) return null;

  const aiChatbot = bundle.aiChatbot;
  if (!aiChatbot || typeof aiChatbot !== 'object') return null;

  const initialReplies = (aiChatbot as Record<string, unknown>).initialReplies;
  if (!initialReplies || typeof initialReplies !== 'object') return null;

  return initialReplies as Record<string, unknown>;
};

interface Props {
  topic: string | null;
  setTopic: (topic: string) => void;
  chatbotConfig: ChatbotConfig;
  onRequestFeedback?: (isWidgetClose?: boolean) => void;
}

type SendMessageOptions = {
  skipGeneration?: boolean;
  assistantContent?: string;
};

const ChatInterface = ({ topic, setTopic, onRequestFeedback, chatbotConfig }: Props) => {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);
  const [message, setMessage] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasSuicideRisk, setHasSuicideRisk] = useState(false);
  const [showSuicideQuestions, setShowSuicideQuestions] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const t = useTranslate();
  const { language } = useLanguage();
  const researchConsent = useResearchConsent(
    language,
    conversationId,
    chatbotConfig.researchConsent
  );
  const { data: localizationBundles } = useLocalization();
  const localizationBundle = localizationBundles?.find(
    (bundle) => bundle.lang === language
  )?.bundle;
  const presetReplies = getPresetReplies(localizationBundle);

  const { mutateAsync: createMessage, reset: resetMessageMutation } = useCreateMessage();
  const { mutateAsync: createConversation, isPending: isCreatingConversation } =
    useCreateConversation();

  const initializationAttempted = useRef(false);
  const suicideMessageId = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const seenInfoCardStackIds = useRef<Set<string>>(new Set());
  const [hideInfoCards, setHideInfoCards] = useState<boolean>(false);
  const [voiceRecorderState, setVoiceRecorderState] = useState<RecordingState>('idle');

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  const addToConversation = useCallback((message: ConversationItem) => {
    setConversation((prev) => [...prev, message]);
  }, []);

  const removeFromConversation = useCallback((messageId: string) => {
    setConversation((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  // Scroll on conversation changes with a slight delay to ensure content is rendered
  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      scrollToBottom();
    }, 100);

    return () => clearTimeout(scrollTimeout);
  }, [conversation, scrollToBottom]);

  useEffect(() => {
    seenInfoCardStackIds.current = new Set();
  }, [conversationId]);

  useEffect(() => {
    async function initializeConversation() {
      if (initializationAttempted.current || !getSessionToken()) return;

      initializationAttempted.current = true;
      setIsInitializing(true);

      // Add loading message immediately
      setConversation([
        {
          id: nanoid(),
          author: 'ASSISTANT',
          content: t('common.labels.writing'),
          time: '',
          options: [],
          feedback: '',
        },
      ]);

      try {
        const response = await createConversation({ language });
        setConversationId(response.id);

        // Replace loading message with welcome message
        const welcomeMessage: ConversationItem = {
          id: nanoid(),
          author: 'SYSTEM',
          content: t('aiChatbot.welcomeMessage'),
          time: new Date().toUTCString(),
          options: [
            t('aiChatbot.initialOptions.option1'),
            t('aiChatbot.initialOptions.option2'),
            t('aiChatbot.initialOptions.option3'),
            t('aiChatbot.initialOptions.option4'),
          ].filter((option) => option.trim()),
        };

        setConversation([welcomeMessage, ...response.messages]);

        scrollToBottom();
      } catch (error) {
        console.error('Error initializing conversation:', error);
        // In case of error, show error message instead of loading
        setConversation([
          {
            id: nanoid(),
            author: 'ASSISTANT',
            content: t('aiChatbot.initializationError'),
            time: new Date().toUTCString(),
            options: [],
            feedback: '',
            isError: true,
          },
        ]);
      } finally {
        setIsInitializing(false);
      }
    }

    initializeConversation();
  }, [scrollToBottom, t, language, createConversation]);

  const editMessageFeedbackById = (id: string, feedback: string) => {
    const updatedFields = { feedback };
    setConversation((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, ...updatedFields } : item))
    );
  };

  const clearSuicideRisk = useCallback(() => {
    setHasSuicideRisk(false);
  }, []);

  const handleCancelMessage = () => {
    if (abortController) {
      // First clean up the UI
      setConversation((prev) => prev.filter((msg) => msg.content !== t('common.labels.writing')));
      setIsSendingMessage(false);
      setAbortController(null);
      // Then abort the request
      abortController.abort();
      // Reset mutation state
      resetMessageMutation();
    }
  };

  const sendMessage = useCallback(
    async (inboundMessage = '', options: SendMessageOptions = {}) => {
      if (isInitializing || !conversationId || isSendingMessage) return;

      const messageToSend = inboundMessage || message;
      if (!messageToSend.trim()) return;

      const skipGeneration = options.skipGeneration === true;
      const assistantContent = options.assistantContent ?? '';

      const buildErrorMessage = (): ConversationItem => ({
        id: nanoid(),
        author: 'ASSISTANT',
        content: t('common.errors.messageNotReceived'),
        time: new Date().toUTCString(),
        options: [],
        isError: true,
      });

      // Validate before sending
      const validation = validateChatMessage(messageToSend, true);

      if (!validation.success) {
        if (validation.error?.includes('||')) {
          const [key, char] = validation.error.split('||');
          setValidationError(t(key || '', { char }));
        } else {
          setValidationError(t(validation.error ?? 'aiChatbot.unknownError'));
        }
        return;
      }

      // Create new AbortController for this request
      const controller = new AbortController();
      setAbortController(controller);

      setIsSendingMessage(true);
      setShowSuicideQuestions(false);

      const applyResponse = (response: ConversationItem, pendingMessageId: string) => {
        const normalizedResponse = suppressDuplicateInfoCards(
          response,
          seenInfoCardStackIds.current
        );
        setConversation((prev) => {
          if (skipGeneration) {
            return prev.map((msg) => (msg.id === pendingMessageId ? normalizedResponse : msg));
          }

          const withoutPending = prev.filter((msg) => msg.id !== pendingMessageId);
          return [...withoutPending, normalizedResponse];
        });

        researchConsent.actions.checkAndShowConsent(
          normalizedResponse,
          addToConversation,
          setConversation
        );

        if (normalizedResponse.safe === false) {
          setHasSuicideRisk(true);
          setShowSuicideQuestions(true);
          suicideMessageId.current = normalizedResponse.id;
        }

        if (normalizedResponse.detectedTopic) {
          setTopic(normalizedResponse.detectedTopic);
        }
      };

      try {
        // Clear options from previous messages
        setConversation((prev) =>
          prev.map((msg) => ({
            ...msg,
            options: [],
          }))
        );

        const userMessage: ConversationItem = {
          id: nanoid(),
          author: 'USER',
          content: messageToSend,
          time: new Date().toUTCString(),
        };

        // Optimistically add the user message to the conversation
        addToConversation(userMessage);

        // Clear the message input
        setMessage('');

        // Add a small delay to simulate the assistant typing
        await wait(TYPING_INDICATOR_DELAY);

        const pendingMessage: ConversationItem = {
          id: nanoid(),
          author: 'ASSISTANT',
          content: t('common.labels.writing'),
          time: '',
          options: [],
          feedback: '',
        };

        addToConversation(pendingMessage);

        const requestParams = {
          conversationId,
          role: 'USER',
          content: messageToSend,
          topic,
          language,
          abortSignal: controller.signal,
          ...(skipGeneration ? { skipGeneration: true, assistantContent } : {}),
        };

        try {
          const responsePromise = createMessage(requestParams);
          const response = skipGeneration
            ? (await Promise.all([responsePromise, wait(PRESET_REPLY_POST_TYPING_DELAY)]))[0]
            : await responsePromise;

          if (controller.signal.aborted) {
            return;
          }

          if (
            response &&
            typeof response === 'object' &&
            'aborted' in response &&
            response.aborted
          ) {
            return;
          }

          applyResponse(response, pendingMessage.id);
        } catch (error) {
          if (controller.signal.aborted) {
            return;
          }

          console.error('Error sending message:', error);

          removeFromConversation(pendingMessage.id);
          addToConversation(buildErrorMessage());
        }
      } catch (error) {
        console.error('Error in sendMessage setup:', error);
      } finally {
        // Always clean up, but only if not aborted
        if (!controller.signal.aborted) {
          setIsSendingMessage(false);
          setAbortController(null);
        }
      }
    },
    [
      isInitializing,
      conversationId,
      isSendingMessage,
      message,
      t,
      topic,
      language,
      setTopic,
      createMessage,
      researchConsent.actions,
      addToConversation,
      removeFromConversation,
    ]
  );

  const handleOptionClick = useCallback(
    (option: string) => {
      if (presetReplies) {
        const matchedEntry = Object.entries(presetReplies).find(([key, value]) => {
          if (typeof value !== 'string' || !value.trim()) return false;
          return option === t(`aiChatbot.initialOptions.${key}`);
        });

        if (matchedEntry) {
          const [, presetReply] = matchedEntry as [string, string];
          void sendMessage(option, { skipGeneration: true, assistantContent: presetReply });
          return;
        }
      }

      void sendMessage(option);
    },
    [presetReplies, sendMessage, t]
  );

  useEffect(() => {
    const handleClose = (event: MessageEvent) => {
      if (event.data === 'widget:close') {
        onRequestFeedback?.(true);
      }
    };

    window.addEventListener('message', handleClose);
    return () => window.removeEventListener('message', handleClose);
  }, [onRequestFeedback]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'widget:requestFeedback') {
        onRequestFeedback?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onRequestFeedback]);

  // Update the onHideInfoCards handler to call the backend service
  const handleHideInfoCards = (messageId: string) => {
    setHideInfoCards(true);

    // Call backend to update the database
    if (messageId) {
      try {
        hideInfoCardsForMessage({ messageId }).catch(() => {
          // Error handled silently
        });
      } catch {
        // Error handled silently
      }
    }
  };

  // Add a cleanup effect to abort any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  return (
    <div className="relative h-full flex flex-col bg-background">
      {/* Main scrollable content area */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto min-h-0"
        role="log"
        aria-live="polite"
      >
        <div className="mx-auto max-w-2xl px-4 pb-4">
          {conversation.map((message, index) => {
            const prevMessage = index > 0 ? conversation[index - 1] : null;
            const displayHeader = !(
              message.author === 'USER' &&
              prevMessage &&
              prevMessage.author === 'USER'
            );

            if (!(message.content || message.special)) return null;

            // Check if this is a research consent message
            const isConsentPromptActive = message.id === researchConsent.state.messageId;

            // Choose the appropriate component based on message type
            if (message.author === 'USER') {
              return (
                <UserMessage
                  key={message.id || index}
                  content={message.content}
                  time={message.time}
                  displayHeader={displayHeader}
                />
              );
            } else if (isConsentPromptActive) {
              return (
                <ResearchConsentMessage
                  key={message.id || index}
                  content={message.content}
                  isExpanded={researchConsent.state.isExpanded}
                  onToggle={researchConsent.actions.handleConsentToggle}
                  onResponse={(consentGiven: boolean) =>
                    researchConsent.actions.handleConsentResponse(consentGiven, setConversation)
                  }
                  hasResponded={researchConsent.state.hasResponded}
                  currentConsent={researchConsent.state.currentConsent}
                />
              );
            } else {
              return (
                <AssistantMessage
                  key={message.id || index}
                  {...message}
                  id={message.id}
                  content={message.content}
                  time={message.time}
                  options={message.options}
                  feedback={message.feedback}
                  showInfoCards={!!message.showInfoCards}
                  infoCardsContext={message.infoCardsContext}
                  onOptionClick={handleOptionClick}
                  editMessageFeedbackById={editMessageFeedbackById}
                  topic={topic}
                  ref={chatContainerRef}
                  hideInfoCards={hideInfoCards}
                  onHideInfoCards={() => handleHideInfoCards(message.id)}
                  displayHeader={displayHeader}
                  isResearchConsentMessage={message.id === researchConsent.state.messageId}
                />
              );
            }
          })}

          {showSuicideQuestions && (
            <div className="mt-4">
              <SuicideQuestions
                onClearSuicideRisk={clearSuicideRisk}
                messageId={suicideMessageId.current}
              />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <ChatInput
        message={message}
        setMessage={setMessage}
        onSendMessage={sendMessage}
        onCancelMessage={handleCancelMessage}
        hasSuicideRisk={hasSuicideRisk}
        isSendingMessage={isSendingMessage}
        isCreatingConversation={isCreatingConversation}
        validationError={validationError}
        setValidationError={setValidationError}
        voiceRecorderState={voiceRecorderState}
        setVoiceRecorderState={setVoiceRecorderState}
        conversationId={conversationId}
      />
    </div>
  );
};

export default ChatInterface;
